// widget/src/components/EmailCapture.tsx
import React, { useState } from 'react';

interface EmailCaptureProps {
  onSubmit: (email: string) => void;
  primaryColor: string;
}

export const EmailCapture: React.FC<EmailCaptureProps> = ({ onSubmit, primaryColor }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [activeQuickStart, setActiveQuickStart] = useState<number | null>(null);

  // Animate in on mount
  React.useEffect(() => {
    setTimeout(() => setShowContent(true), 100);
  }, []);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email');
      return;
    }

    setIsLoading(true);
    setTimeout(() => {
      onSubmit(email.trim().toLowerCase());
    }, 300);
  };

  const quickStartOptions = [
    { icon: '💰', label: 'Grow my practice profits' },
    { icon: '🔬', label: 'Learn MIS surgery' },
    { icon: '📈', label: 'Get more patients' },
  ];

  return (
    <div
      style={{
        padding: '20px 24px',
        opacity: showContent ? 1 : 0,
        transform: showContent ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 0.4s ease-out',
      }}
    >
      {/* Animated Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        {/* Animated avatar orb with spinning ring */}
        <div
          style={{
            position: 'relative',
            width: '64px',
            height: '64px',
            margin: '0 auto 12px',
          }}
        >
          {/* Spinning gradient ring */}
          <div
            style={{
              position: 'absolute',
              inset: '-3px',
              borderRadius: '50%',
              background: `conic-gradient(from 0deg, ${primaryColor}, ${primaryColor}20, ${primaryColor})`,
              animation: 'novaSpin 3s linear infinite',
            }}
          />
          {/* Inner white circle */}
          <div
            style={{
              position: 'absolute',
              inset: '2px',
              borderRadius: '50%',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke={primaryColor}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2-2z" />
            </svg>
          </div>
          {/* Green online indicator */}
          <div
            style={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              border: '3px solid white',
              animation: 'novaGlow 1.5s ease-in-out infinite',
            }}
          />
        </div>

        <h3
          style={{
            margin: '0 0 4px',
            fontSize: '20px',
            fontWeight: '700',
            color: '#1a1a1a',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          Hey there! 👋
        </h3>

        <p
          style={{
            margin: '0 0 4px',
            fontSize: '15px',
            fontWeight: '600',
            color: primaryColor,
          }}
        >
          I'm Nova, Dr. TJ's AI
        </p>

        <p
          style={{
            margin: 0,
            fontSize: '14px',
            color: '#666',
            lineHeight: '1.5',
          }}
        >
          Ask me anything about growing your practice, MIS training, or finding the right program for you.
        </p>
      </div>

      {/* Quick Start Chips */}
      <div style={{ marginBottom: '20px' }}>
        <p
          style={{
            fontSize: '12px',
            color: '#999',
            marginBottom: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: '600',
          }}
        >
          I can help you:
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {quickStartOptions.map((option, index) => (
            <button
              key={index}
              type="button"
              onMouseEnter={() => setActiveQuickStart(index)}
              onMouseLeave={() => setActiveQuickStart(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '20px',
                border: `1.5px solid ${activeQuickStart === index ? primaryColor : '#e5e7eb'}`,
                backgroundColor: activeQuickStart === index ? `${primaryColor}10` : 'white',
                fontSize: '13px',
                fontWeight: '500',
                color: activeQuickStart === index ? primaryColor : '#555',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                transform: activeQuickStart === index ? 'scale(1.02)' : 'scale(1)',
              }}
            >
              <span>{option.icon}</span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Email Form */}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '12px' }}>
          <label
            htmlFor="email"
            style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#333'
            }}
          >
            What's your email?
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            placeholder="you@example.com"
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: '16px',
              border: `2px solid ${error ? '#ef4444' : '#e5e7eb'}`,
              borderRadius: '12px',
              outline: 'none',
              transition: 'all 0.2s ease',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = primaryColor;
              e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = error ? '#ef4444' : '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
          {error && (
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#ef4444' }}>
              {error}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '14px 20px',
            fontSize: '16px',
            fontWeight: '600',
            color: 'white',
            backgroundColor: primaryColor,
            border: 'none',
            borderRadius: '12px',
            cursor: isLoading ? 'wait' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
          onMouseOver={(e) => {
            if (!isLoading) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 6px 20px ${primaryColor}40`;
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {isLoading ? (
            <>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ animation: 'novaSpin 1s linear infinite' }}
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Connecting...
            </>
          ) : (
            <>
              Start Conversation
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>
      </form>

      {/* Trust indicator */}
      <p
        style={{
          marginTop: '16px',
          fontSize: '12px',
          color: '#999',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <circle cx="12" cy="16" r="1" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        By continuing, you agree to receive communications from Dr. TJ Ahn's team
      </p>

      {/* Keyframe animations */}
      <style>{`
        @keyframes novaSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes novaGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
          50% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
        }
      `}</style>
    </div>
  );
};

export default EmailCapture;
